import { useMemo } from 'react';

import { Select } from 'antd';
import { useIntl } from 'react-intl';

import Title from '@src/components/BoxContainer/components/Title';
import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import {
  getRealtimeStatusFilterCodesForTab,
  type WorkJourneyRealTimeTab,
} from '@src/packages/workJourney/constants';

import { getAttendanceStatusLabelId } from '../../utils/attendanceStatus/resolveAttendanceStatus';
import type { FilterSelectOption } from '../../utils/filterAttendanceItems';
import { REAL_TIME_FILTER_FIELD } from '../../utils/initRealTimeFilters';

import styles from './WorkJourneyRealTimeFilters.module.less';

type StructuralFilterField =
  | typeof REAL_TIME_FILTER_FIELD.WORK_SHIFT
  | typeof REAL_TIME_FILTER_FIELD.WORK_JOURNEY
  | typeof REAL_TIME_FILTER_FIELD.USER;

type Props = {
  filters: FilterValues;
  handleFilters: (props: SetValueProps) => void;
  onStructuralFilterChange: (field: StructuralFilterField, value: string) => void;
  shiftOptions: FilterSelectOption[];
  journeyOptions: FilterSelectOption[];
  userOptions: FilterSelectOption[];
  activeTab: WorkJourneyRealTimeTab;
};

const WorkJourneyRealTimeFilters = ({
  filters,
  handleFilters,
  onStructuralFilterChange,
  shiftOptions,
  journeyOptions,
  userOptions,
  activeTab,
}: Props) => {
  const intl = useIntl();

  const statusOptions = useMemo(
    () =>
      getRealtimeStatusFilterCodesForTab(activeTab).map(code => {
        const labelId = getAttendanceStatusLabelId(code);
        return {
          value: code,
          label: labelId ? intl.formatMessage({ id: labelId }) : code,
        };
      }),
    [activeTab, intl],
  );

  return (
    <div className={styles.root}>
      <div>
        <Title.LabelForm value={intl.formatMessage({ id: 'workJourney.realTime.filter.shift' })} />
        <Select
          className={styles.field}
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={intl.formatMessage({ id: 'workJourney.realTime.filter.shift.placeholder' })}
          options={shiftOptions}
          value={(filters[REAL_TIME_FILTER_FIELD.WORK_SHIFT]?.value as string) || undefined}
          onChange={value => {
            onStructuralFilterChange(REAL_TIME_FILTER_FIELD.WORK_SHIFT, value ?? '');
          }}
          getPopupContainer={trigger => trigger.parentNode as HTMLElement}
          id={REAL_TIME_FILTER_FIELD.WORK_SHIFT}
        />
      </div>

      <div>
        <Title.LabelForm
          value={intl.formatMessage({ id: 'workJourney.realTime.filter.journey' })}
        />
        <Select
          className={styles.field}
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={intl.formatMessage({
            id: 'workJourney.realTime.filter.journey.placeholder',
          })}
          options={journeyOptions}
          value={(filters[REAL_TIME_FILTER_FIELD.WORK_JOURNEY]?.value as string) || undefined}
          onChange={value => {
            onStructuralFilterChange(REAL_TIME_FILTER_FIELD.WORK_JOURNEY, value ?? '');
          }}
          getPopupContainer={trigger => trigger.parentNode as HTMLElement}
          id={REAL_TIME_FILTER_FIELD.WORK_JOURNEY}
        />
      </div>

      <div>
        <Title.LabelForm value={intl.formatMessage({ id: 'workJourney.realTime.filter.user' })} />
        <Select
          className={styles.field}
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={intl.formatMessage({ id: 'workJourney.realTime.filter.user.placeholder' })}
          options={userOptions}
          value={(filters[REAL_TIME_FILTER_FIELD.USER]?.value as string) || undefined}
          onChange={value => {
            onStructuralFilterChange(REAL_TIME_FILTER_FIELD.USER, value ?? '');
          }}
          getPopupContainer={trigger => trigger.parentNode as HTMLElement}
          id={REAL_TIME_FILTER_FIELD.USER}
        />
      </div>

      <div>
        <Title.LabelForm value={intl.formatMessage({ id: 'workJourney.realTime.filter.status' })} />
        <Select
          className={styles.field}
          allowClear
          placeholder={intl.formatMessage({
            id: 'workJourney.realTime.filter.status.placeholder',
          })}
          options={statusOptions}
          value={(filters[REAL_TIME_FILTER_FIELD.STATUS]?.value as string) || undefined}
          onChange={value => {
            const code = value ?? '';
            const selectedLabel = statusOptions.find(option => option.value === code)?.label ?? '';
            handleFilters({
              key: REAL_TIME_FILTER_FIELD.STATUS,
              value: code,
              label: intl.formatMessage({ id: 'workJourney.realTime.filter.status' }),
              valueTranslated: selectedLabel,
              valueType: 'string',
            });
          }}
          getPopupContainer={trigger => trigger.parentNode as HTMLElement}
          id={REAL_TIME_FILTER_FIELD.STATUS}
        />
      </div>
    </div>
  );
};

export default WorkJourneyRealTimeFilters;
